import React, { lazy, Suspense, useEffect } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom";
import { Toaster as SonnerToaster } from "@/components/ui/sonner";
import { Toaster as ShadToaster } from "@/components/ui/toaster";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { EuiProvider } from "@elastic/eui";

import { AuthProvider } from "@/contexts/AuthContext";
import { EmpresaProvider } from "@/contexts/EmpresaContext";
import { ProfissionalProvider } from "@/contexts/ProfissionalContext";
import { AdminAuthProvider } from "@/contexts/AdminAuthContext";
import { ProtectedRoute } from "@/components/auth/ProtectedRoute";
import { AdminRoutes } from "@/components/admin/AdminRoutes";
import { PermissionGuard } from "@/components/auth/RoleGuard";
import type { Modulo } from "@/hooks/useTeamPermissions";
import { LogoutOverlay } from "@/components/auth/LogoutOverlay";

/**
 * Wraps React.lazy but keeps a reference to the import factory so we can
 * pre-fetch the chunk in the background (during idle time) after the app
 * mounts. This makes subsequent route navigations near-instant instead of
 * showing the "Carregando..." fallback while the chunk is downloaded.
 */
const lazyImporters: Array<() => Promise<unknown>> = [];
function lazyRoute<T extends React.ComponentType<any>>(
  factory: () => Promise<{ default: T }>
) {
  lazyImporters.push(factory);
  return lazy(factory);
}

function preloadRoutes() {
  const run = () => {
    for (const importer of lazyImporters) {
      // fire and forget; Vite dedupes so already-loaded chunks are no-ops
      importer().catch(() => {});
    }
  };
  const w = window as any;
  if (typeof w.requestIdleCallback === "function") {
    w.requestIdleCallback(run, { timeout: 2000 });
  } else {
    setTimeout(run, 500);
  }
}

function RoutePreloader() {
  useEffect(() => {
    preloadRoutes();
  }, []);
  return null;
}

/**
 * Rede de segurança: bibliotecas de overlay (Radix Dialog/Sheet/Drawer, vaul)
 * travam o scroll do <body> enquanto abertas e devem restaurá-lo ao fechar.
 * Se uma navegação interromper esse ciclo antes da limpeza rodar (comum no
 * Safari/iOS quando o fechamento é disparado pelo próprio Link de navegação),
 * o body pode ficar preso com overflow/transform residual, impedindo o
 * scroll por toque em qualquer página seguinte. Resetar esses estilos a cada
 * troca de rota garante que a página que acabou de abrir nunca herde um
 * travamento de uma tela anterior.
 */
function ScrollLockGuard() {
  const location = useLocation();
  useEffect(() => {
    const { body } = document;
    body.style.removeProperty("overflow");
    body.style.removeProperty("position");
    body.style.removeProperty("touch-action");
    body.style.removeProperty("transform");
    body.style.removeProperty("padding-right");
  }, [location.pathname]);
  return null;
}

function RouteFallback() {
  // Subtle top progress bar instead of a full-screen "Carregando..." blank
  // screen — the previous UI stays visible underneath while the next chunk
  // (usually already preloaded) resolves.
  return (
    <div className="fixed top-0 left-0 right-0 z-[9999] h-0.5 overflow-hidden bg-transparent pointer-events-none">
      <div className="h-full w-1/3 bg-primary/70 animate-[loadingbar_1s_ease-in-out_infinite]" />
    </div>
  );
}

type Acao = "ver" | "criar" | "editar" | "excluir";

const CanAccess = ({ modulo, acao = "ver", children }: { modulo: Modulo; acao?: Acao; children: React.ReactNode }) => (
  <PermissionGuard modulo={modulo} acao={acao}>{children}</PermissionGuard>
);


const Index = lazyRoute(() => import("@/pages/Index"));
const Plans = lazyRoute(() => import("@/pages/Plans"));
const PrivacyPolicy = lazyRoute(() => import("@/pages/PrivacyPolicy"));
const Login = lazyRoute(() => import("@/pages/Login"));
const ForgotPassword = lazyRoute(() => import("@/pages/ForgotPassword"));
const ResetPassword = lazyRoute(() => import("@/pages/ResetPassword"));
const Dashboard = lazyRoute(() => import("@/pages/Dashboard"));
const Clients = lazyRoute(() => import("@/pages/Clients"));
const ClientNew = lazyRoute(() => import("@/pages/ClientNew"));
const ClientDetail = lazyRoute(() => import("@/pages/ClientDetail"));
const Services = lazyRoute(() => import("@/pages/Services"));
const ServiceNew = lazyRoute(() => import("@/pages/ServiceNew"));
const ServiceDetail = lazyRoute(() => import("@/pages/ServiceDetail"));
const Treatments = lazyRoute(() => import("@/pages/Treatments"));
const TreatmentNew = lazyRoute(() => import("@/pages/TreatmentNew"));
const TreatmentDetail = lazyRoute(() => import("@/pages/TreatmentDetail"));
const TreatmentAnamneseFill = lazyRoute(() => import("@/pages/TreatmentAnamneseFill"));
const AppointmentPreAnamneseFill = lazyRoute(() => import("@/pages/AppointmentPreAnamneseFill"));
const PublicPreAnamneseFill = lazyRoute(() => import("@/pages/PublicPreAnamneseFill"));
const PublicAnamneseView = lazyRoute(() => import("@/pages/PublicAnamneseView"));
const PublicContratoSign = lazyRoute(() => import("@/pages/PublicContratoSign"));
const PublicBooking = lazyRoute(() => import("@/pages/PublicBooking"));
const Appointments = lazyRoute(() => import("@/pages/Appointments"));
const AppointmentNew = lazyRoute(() => import("@/pages/AppointmentNew"));

const Budgets = lazyRoute(() => import("@/pages/Budgets"));
const BudgetNew = lazyRoute(() => import("@/pages/BudgetNew"));
const BudgetDetail = lazyRoute(() => import("@/pages/BudgetDetail"));
const BudgetEdit = lazyRoute(() => import("@/pages/BudgetEdit"));
const BudgetPdfSettings = lazyRoute(() => import("@/pages/BudgetPdfSettings"));
const Financial = lazyRoute(() => import("@/pages/Financial"));
const FinancialNew = lazyRoute(() => import("@/pages/FinancialNew"));
const FinancialDetail = lazyRoute(() => import("@/pages/FinancialDetail"));
const FinancialEdit = lazyRoute(() => import("@/pages/FinancialEdit"));
const FinancialCategories = lazyRoute(() => import("@/pages/FinancialCategories"));
const Gifts = lazyRoute(() => import("@/pages/Gifts"));
const GiftNew = lazyRoute(() => import("@/pages/GiftNew"));
const GiftDetail = lazyRoute(() => import("@/pages/GiftDetail"));
const GiftEdit = lazyRoute(() => import("@/pages/GiftEdit"));
const Products = lazyRoute(() => import("@/pages/Products"));
const ProductNew = lazyRoute(() => import("@/pages/ProductNew"));
const ProductEdit = lazyRoute(() => import("@/pages/ProductEdit"));
const ProductDetail = lazyRoute(() => import("@/pages/ProductDetail"));
const Insumos = lazyRoute(() => import("@/pages/Insumos"));
const InsumoNew = lazyRoute(() => import("@/pages/InsumoNew"));
const InsumoDetail = lazyRoute(() => import("@/pages/InsumoDetail"));
const InsumoEdit = lazyRoute(() => import("@/pages/InsumoEdit"));
const Equipment = lazyRoute(() => import("@/pages/Equipment"));
const EquipmentNew = lazyRoute(() => import("@/pages/EquipmentNew"));
const EquipmentDetail = lazyRoute(() => import("@/pages/EquipmentDetail"));
const EquipmentEdit = lazyRoute(() => import("@/pages/EquipmentEdit"));
const PartnersCompanies = lazyRoute(() => import("@/pages/PartnersCompanies"));
const PartnerCompanyNew = lazyRoute(() => import("@/pages/PartnerCompanyNew"));
const PartnerCompanyDetail = lazyRoute(() => import("@/pages/PartnerCompanyDetail"));
const PartnerCompanyEdit = lazyRoute(() => import("@/pages/PartnerCompanyEdit"));
const Suppliers = lazyRoute(() => import("@/pages/Suppliers"));
const SupplierNew = lazyRoute(() => import("@/pages/SupplierNew"));
const SupplierEdit = lazyRoute(() => import("@/pages/SupplierEdit"));
const SupplierDetail = lazyRoute(() => import("@/pages/SupplierDetail"));
const Promotions = lazyRoute(() => import("@/pages/Promotions"));
const PhotoGallery = lazyRoute(() => import("@/pages/PhotoGallery"));
const Notifications = lazyRoute(() => import("@/pages/Notifications"));
const Negotiations = lazyRoute(() => import("@/pages/Negotiations"));
const NegotiationDetail = lazyRoute(() => import("@/pages/NegotiationDetail"));
const Avaliacoes = lazyRoute(() => import("@/pages/Avaliacoes"));
const AvaliacaoNew = lazyRoute(() => import("@/pages/AvaliacaoNew"));
const AvaliacaoEdit = lazyRoute(() => import("@/pages/AvaliacaoEdit"));
const AvaliacaoDetail = lazyRoute(() => import("@/pages/AvaliacaoDetail"));
const NotFound = lazyRoute(() => import("@/pages/NotFound"));
const Profile = lazyRoute(() => import("@/pages/Profile"));
const Settings = lazyRoute(() => import("@/pages/Settings"));
const AnamneseTemplates = lazyRoute(() => import("@/pages/AnamneseTemplates"));
const AnamneseNew = lazyRoute(() => import("@/pages/AnamneseNew"));
const AnamneseBuilder = lazyRoute(() => import("@/pages/AnamneseBuilder"));
const AnamnesesCentral = lazyRoute(() => import("@/pages/AnamnesesCentral"));
const AnamneseInstanceView = lazyRoute(() => import("@/pages/AnamneseInstanceView"));
const Contratos = lazyRoute(() => import("@/pages/Contratos"));
const ContratosBuilder = lazyRoute(() => import("@/pages/ContratosBuilder"));
const Clube = lazyRoute(() => import("@/pages/Clube"));
const ClubeNovo = lazyRoute(() => import("@/pages/ClubeNovo"));
const ClubeDetail = lazyRoute(() => import("@/pages/ClubeDetail"));
const ClubeAssinaturas = lazyRoute(() => import("@/pages/ClubeAssinaturas"));
const ClubeAssinaturaNova = lazyRoute(() => import("@/pages/ClubeAssinaturaNova"));
const ClubeAssinaturaDetail = lazyRoute(() => import("@/pages/ClubeAssinaturaDetail"));
const ClubeRelatorios = lazyRoute(() => import("@/pages/ClubeRelatorios"));
const Team = lazyRoute(() => import("@/pages/Team"));
const TeamMemberDetail = lazyRoute(() => import("@/pages/TeamMemberDetail"));
const ComissoesRelatorio = lazyRoute(() => import("@/pages/ComissoesRelatorio"));
const InsumosRelatorio = lazyRoute(() => import("@/pages/InsumosRelatorio"));
const InviteAccept = lazyRoute(() => import("@/pages/InviteAccept"));
const WhatsApp = lazyRoute(() => import("@/pages/WhatsApp"));

// OTIMIZADO: QueryClient com caching agressivo para reduzir requests
function ShareTokenRedirect() {
  const { token } = useParams();
  return <Navigate to={`/pre-atendimento/${token ?? ""}`} replace />;
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 1000 * 60 * 10, // 10 minutos - dados ficam frescos por mais tempo
      gcTime: 1000 * 60 * 30, // 30 minutos - cache na memória
      refetchOnWindowFocus: false, // Não refazer query ao focar janela
      refetchOnMount: false, // Não refazer query ao montar se já tem cache válido
      refetchOnReconnect: false, // Não refazer query ao reconectar
    },
  },
});

function App() {
  console.log("🚀 App: App function called");
  return (
    <QueryClientProvider client={queryClient}>
      <EuiProvider colorMode="light">
        <AuthProvider>
          <EmpresaProvider>
            <ProfissionalProvider>
            <div className="min-h-screen bg-background font-sans antialiased">
              <SonnerToaster position="top-right" />
              <ShadToaster />
              <BrowserRouter>
                <LogoutOverlay />
                <RoutePreloader />
                <ScrollLockGuard />
                <Suspense fallback={<RouteFallback />}><Routes>
                  {/* Public routes - accessible to everyone */}
                  <Route path="/" element={<Index />} />
                  <Route path="/planos" element={<Plans />} />
                  <Route path="/politica-de-privacidade" element={<PrivacyPolicy />} />
                  <Route path="/login" element={<Login />} />
                  <Route path="/forgot-password" element={<ForgotPassword />} />
                  <Route path="/reset-password" element={<ResetPassword />} />
                  <Route path="/convite/:token" element={<InviteAccept />} />
                  <Route
                    path="/pre-atendimento/:token"
                    element={<PublicPreAnamneseFill />}
                  />
                  <Route
                    path="/anamnese/view/:token"
                    element={<PublicAnamneseView />}
                  />
                  <Route
                    path="/p/contratos/:id"
                    element={<PublicContratoSign />}
                  />
                  <Route
                    path="/agendar/:token"
                    element={<PublicBooking />}
                  />
                  <Route
                    path="/share/:token"
                    element={<ShareTokenRedirect />}
                  />

                  {/* Admin routes */}
                  <Route
                    path="/admin/*"
                    element={
                      <AdminAuthProvider>
                        <AdminRoutes />
                      </AdminAuthProvider>
                    }
                  />

                  {/* Protected routes - require authentication and proper access */}
                  <Route element={<ProtectedRoute />}>
                    <Route path="/home" element={<Dashboard />} />
                    <Route
                      path="/dashboard"
                      element={<Navigate to="/home" replace />}
                    />
                    <Route path="/profile" element={<Profile />} />
                    <Route path="/settings" element={<Settings />} />
                    <Route path="/clients" element={<CanAccess modulo="clientes"><Clients /></CanAccess>} />
                    <Route path="/clients/new" element={<CanAccess modulo="clientes" acao="criar"><ClientNew /></CanAccess>} />
                    <Route path="/clients/:id" element={<CanAccess modulo="clientes"><ClientDetail /></CanAccess>} />
                    <Route path="/services" element={<CanAccess modulo="servicos"><Services /></CanAccess>} />
                    <Route path="/services/new" element={<CanAccess modulo="servicos" acao="criar"><ServiceNew /></CanAccess>} />
                    <Route path="/services/:id" element={<CanAccess modulo="servicos"><ServiceDetail /></CanAccess>} />

                    <Route path="/treatments" element={<CanAccess modulo="atendimentos"><Treatments /></CanAccess>} />
                    <Route path="/treatments/new" element={<CanAccess modulo="atendimentos" acao="criar"><TreatmentNew /></CanAccess>} />
                    <Route
                      path="/treatments/:id"
                      element={<CanAccess modulo="atendimentos"><TreatmentDetail /></CanAccess>}
                    />
                    <Route
                      path="/treatments/:id/anamnese"
                      element={<CanAccess modulo="anamnese" acao="editar"><TreatmentAnamneseFill /></CanAccess>}
                    />
                    <Route path="/appointments" element={<CanAccess modulo="agenda"><Appointments /></CanAccess>} />
                    <Route
                      path="/appointments/new"
                      element={<CanAccess modulo="agenda" acao="criar"><AppointmentNew /></CanAccess>}
                    />
                    <Route
                      path="/appointments/:appointmentId/anamnese"
                      element={<CanAccess modulo="anamnese" acao="editar"><AppointmentPreAnamneseFill /></CanAccess>}
                    />
                    <Route path="/agenda" element={<Navigate to="/appointments" replace />} />
                    <Route path="/budgets" element={<CanAccess modulo="orcamentos"><Budgets /></CanAccess>} />
                    <Route path="/budgets/new" element={<CanAccess modulo="orcamentos" acao="criar"><BudgetNew /></CanAccess>} />
                    <Route
                      path="/budgets/settings/pdf"
                      element={<CanAccess modulo="orcamentos" acao="editar"><BudgetPdfSettings /></CanAccess>}
                    />
                    <Route path="/budgets/:id" element={<CanAccess modulo="orcamentos"><BudgetDetail /></CanAccess>} />
                    <Route path="/budgets/:id/edit" element={<CanAccess modulo="orcamentos" acao="editar"><BudgetEdit /></CanAccess>} />
                    <Route path="/negotiations" element={<CanAccess modulo="negociacoes"><Negotiations /></CanAccess>} />
                    <Route
                      path="/negotiations/:id"
                      element={<CanAccess modulo="negociacoes"><NegotiationDetail /></CanAccess>}
                    />
                    <Route path="/financial" element={<CanAccess modulo="financeiro"><Financial /></CanAccess>} />
                    <Route path="/financial/new" element={<CanAccess modulo="financeiro" acao="criar"><FinancialNew /></CanAccess>} />
                    <Route
                      path="/financial/:id"
                      element={<CanAccess modulo="financeiro"><FinancialDetail /></CanAccess>}
                    />
                    <Route
                      path="/financial/:id/edit"
                      element={<CanAccess modulo="financeiro" acao="editar"><FinancialEdit /></CanAccess>}
                    />
                    <Route
                      path="/financial/categories"
                      element={<CanAccess modulo="financeiro" acao="editar"><FinancialCategories /></CanAccess>}
                    />

                    <Route path="/gifts" element={<CanAccess modulo="brindes"><Gifts /></CanAccess>} />
                    <Route path="/gifts/new" element={<CanAccess modulo="brindes" acao="criar"><GiftNew /></CanAccess>} />
                    <Route path="/gifts/:id" element={<CanAccess modulo="brindes"><GiftDetail /></CanAccess>} />
                    <Route path="/gifts/:id/edit" element={<CanAccess modulo="brindes" acao="editar"><GiftEdit /></CanAccess>} />
                    <Route path="/products" element={<CanAccess modulo="produtos"><Products /></CanAccess>} />
                    <Route path="/products/new" element={<CanAccess modulo="produtos" acao="criar"><ProductNew /></CanAccess>} />
                    <Route path="/products/:id" element={<CanAccess modulo="produtos"><ProductDetail /></CanAccess>} />
                    <Route
                      path="/products/:id/edit"
                      element={<CanAccess modulo="produtos" acao="editar"><ProductEdit /></CanAccess>}
                    />
                    <Route path="/insumos" element={<CanAccess modulo="insumos"><Insumos /></CanAccess>} />
                    <Route path="/insumos/relatorio" element={<CanAccess modulo="insumos"><InsumosRelatorio /></CanAccess>} />
                    <Route path="/insumos/new" element={<CanAccess modulo="insumos" acao="criar"><InsumoNew /></CanAccess>} />
                    <Route path="/insumos/:id" element={<CanAccess modulo="insumos"><InsumoDetail /></CanAccess>} />
                    <Route path="/insumos/:id/edit" element={<CanAccess modulo="insumos" acao="editar"><InsumoEdit /></CanAccess>} />
                    <Route path="/equipment" element={<CanAccess modulo="equipamentos"><Equipment /></CanAccess>} />
                    <Route path="/equipment/new" element={<CanAccess modulo="equipamentos" acao="criar"><EquipmentNew /></CanAccess>} />
                    <Route
                      path="/equipment/:id"
                      element={<CanAccess modulo="equipamentos"><EquipmentDetail /></CanAccess>}
                    />
                    <Route
                      path="/equipment/:id/edit"
                      element={<CanAccess modulo="equipamentos" acao="editar"><EquipmentEdit /></CanAccess>}
                    />
                    <Route path="/partners" element={<CanAccess modulo="parceiras"><PartnersCompanies /></CanAccess>} />
                    <Route
                      path="/partners/new"
                      element={<CanAccess modulo="parceiras" acao="criar"><PartnerCompanyNew /></CanAccess>}
                    />
                    <Route
                      path="/partners/:id"
                      element={<CanAccess modulo="parceiras"><PartnerCompanyDetail /></CanAccess>}
                    />
                    <Route
                      path="/partners/:id/edit"
                      element={<CanAccess modulo="parceiras" acao="editar"><PartnerCompanyEdit /></CanAccess>}
                    />
                    <Route
                      path="/partners-companies"
                      element={<CanAccess modulo="parceiras"><PartnersCompanies /></CanAccess>}
                    />
                    <Route path="/suppliers" element={<CanAccess modulo="fornecedores"><Suppliers /></CanAccess>} />
                    <Route path="/suppliers/new" element={<CanAccess modulo="fornecedores" acao="criar"><SupplierNew /></CanAccess>} />
                    <Route path="/suppliers/:id" element={<CanAccess modulo="fornecedores"><SupplierDetail /></CanAccess>} />
                    <Route
                      path="/suppliers/:id/edit"
                      element={<CanAccess modulo="fornecedores" acao="editar"><SupplierEdit /></CanAccess>}
                    />
                    <Route path="/campaigns" element={<CanAccess modulo="orcamentos"><Promotions /></CanAccess>} />
                    <Route path="/promotions" element={<CanAccess modulo="orcamentos"><Promotions /></CanAccess>} />
                    <Route path="/gallery" element={<CanAccess modulo="galeria"><PhotoGallery /></CanAccess>} />
                    <Route path="/notifications" element={<CanAccess modulo="notificacoes"><Notifications /></CanAccess>} />
                    <Route path="/avaliacoes" element={<CanAccess modulo="avaliacoes"><Avaliacoes /></CanAccess>} />
                    <Route path="/avaliacoes/new" element={<CanAccess modulo="avaliacoes" acao="criar"><AvaliacaoNew /></CanAccess>} />
                    <Route
                      path="/avaliacoes/:id"
                      element={<CanAccess modulo="avaliacoes"><AvaliacaoDetail /></CanAccess>}
                    />
                    <Route
                      path="/avaliacoes/:id/edit"
                      element={<CanAccess modulo="avaliacoes" acao="editar"><AvaliacaoEdit /></CanAccess>}
                    />

                    <Route path="/anamneses" element={<CanAccess modulo="anamnese"><AnamneseTemplates /></CanAccess>} />
                    <Route path="/anamneses/central" element={<CanAccess modulo="anamnese"><AnamnesesCentral /></CanAccess>} />
                    <Route path="/anamneses/new" element={<CanAccess modulo="anamnese" acao="criar"><AnamneseNew /></CanAccess>} />
                    <Route path="/anamnese/instance/:instanceId" element={<CanAccess modulo="anamnese"><AnamneseInstanceView /></CanAccess>} />
                    <Route path="/anamneses/instance/:instanceId" element={<CanAccess modulo="anamnese"><AnamneseInstanceView /></CanAccess>} />
                    <Route
                      path="/anamneses/:id"
                      element={<CanAccess modulo="anamnese" acao="editar"><AnamneseBuilder /></CanAccess>}
                    />
                    <Route path="/contratos" element={<CanAccess modulo="contratos"><Contratos /></CanAccess>} />
                    <Route path="/contratos/:id" element={<CanAccess modulo="contratos" acao="editar"><ContratosBuilder /></CanAccess>} />
                    <Route path="/clube" element={<CanAccess modulo="clube"><ClubeAssinaturas /></CanAccess>} />
                    <Route path="/clube/assinaturas/nova" element={<CanAccess modulo="clube" acao="criar"><ClubeAssinaturaNova /></CanAccess>} />
                    <Route path="/clube/assinaturas/:id" element={<CanAccess modulo="clube"><ClubeAssinaturaDetail /></CanAccess>} />
                    <Route path="/clube/modelos" element={<CanAccess modulo="clube"><Clube /></CanAccess>} />
                    <Route path="/clube/modelos/novo" element={<CanAccess modulo="clube" acao="criar"><ClubeNovo /></CanAccess>} />
                    <Route path="/clube/modelos/:id" element={<CanAccess modulo="clube"><ClubeDetail /></CanAccess>} />
                    <Route path="/clube/relatorios" element={<CanAccess modulo="clube"><ClubeRelatorios /></CanAccess>} />
                    <Route path="/team" element={<CanAccess modulo="equipe"><Team /></CanAccess>} />
                    <Route path="/team/:id" element={<CanAccess modulo="equipe" acao="editar"><TeamMemberDetail /></CanAccess>} />
                    <Route path="/team/comissoes" element={<CanAccess modulo="equipe"><ComissoesRelatorio /></CanAccess>} />
                    <Route path="/whatsapp" element={<CanAccess modulo="whatsapp"><WhatsApp /></CanAccess>} />
                  </Route>

                  {/* Catch all route */}
                  <Route path="*" element={<NotFound />} />
                </Routes></Suspense>
              </BrowserRouter>
            </div>
            </ProfissionalProvider>
          </EmpresaProvider>
        </AuthProvider>
      </EuiProvider>
    </QueryClientProvider>
  );
}

export default App;
